import React, { useState, useEffect, useCallback } from 'react';
import { basketballShooting } from '../../utils/basketballShooting';
import { useParams, useNavigate } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import { getPublicProfile } from '../../services/api/userService';
import { apiClient } from '../../services/api/apiService';
import followService from '../../services/api/followService';
import friendService from '../../services/api/friendService';
import PlayerSafetyMenu from './PlayerSafetyMenu';
import '../../styles/features/playerProfile.css';

// ── sport-specific metric display config ─────────────────────────────────────
// Keys map to sport_specific_stats JSONB keys stored by the scoring engine.
const SPORT_METRICS = {
  badminton: [
    { key: 'smashWinner',      label: 'Smash Winners' },
    { key: 'dropWinner',       label: 'Drop Winners' },
    { key: 'crossCourtWinner', label: 'XC Winners' },
    { key: 'netKill',          label: 'Net Kills' },
    { key: 'otherWinner',      label: 'Other Winners' },
    { key: 'serviceAce',       label: 'Service Aces' },
    { key: 'serviceFault',     label: 'Service Faults' },
    { key: 'foul',             label: 'Faults' },
    { key: 'sideOut',          label: 'Side-Outs' },
    { key: 'error',            label: 'Errors' },
    { key: 'unforcedError',    label: 'Unforced Errors' },
    { key: 'forcedError',      label: 'Forced Errors' },
  ],
  badminton_doubles: [
    { key: 'smashWinner',      label: 'Smash Winners' },
    { key: 'dropWinner',       label: 'Drop Winners' },
    { key: 'crossCourtWinner', label: 'XC Winners' },
    { key: 'netKill',          label: 'Net Kills' },
    { key: 'otherWinner',      label: 'Other Winners' },
    { key: 'serviceAce',       label: 'Service Aces' },
    { key: 'serviceFault',     label: 'Service Faults' },
    { key: 'foul',             label: 'Faults' },
    { key: 'sideOut',          label: 'Side-Outs' },
    { key: 'unforcedError',    label: 'Unforced Errors' },
    { key: 'forcedError',      label: 'Forced Errors' },
  ],
  volleyball: [
    { key: 'kill',             label: 'Kills' },
    { key: 'attackError',      label: 'Attack Errors' },
    { key: 'ace',              label: 'Aces' },
    { key: 'blockSolo',        label: 'Block (Solo)' },
    { key: 'blockAssist',      label: 'Block (Assist)' },
    { key: 'dig',              label: 'Digs' },
    { key: 'assist',           label: 'Assists' },
    { key: 'setterDump',       label: 'Setter Dumps' },
    { key: 'tip',              label: 'Tips' },
    { key: 'placementShot',    label: 'Placement Shots' },
    { key: 'serviceError',     label: 'Service Errors' },
    { key: 'foul',             label: 'Fouls' },
  ],
  // Points come from points_scored ("Pts Scored"); the legacy 'points'
  // counter was one per scoring tap, so it is not shown (PR-B13).
  basketball: [
    { key: 'threePointerMade', label: '3PT Made' },
    { key: 'offensiveRebound', label: 'Off. Rebounds' },
    { key: 'defensiveRebound', label: 'Def. Rebounds' },
    { key: 'assist',           label: 'Assists' },
    { key: 'steal',            label: 'Steals' },
    { key: 'blockShot',        label: 'Blocks' },
    { key: 'turnover',         label: 'Turnovers' },
    { key: 'foul',             label: 'Fouls' },
  ],
  football: [
    { key: 'goal',             label: 'Goals' },
    { key: 'ownGoal',          label: 'Own Goals' },
    { key: 'assist',           label: 'Assists' },
    { key: 'tackle',           label: 'Tackles' },
    { key: 'save',             label: 'Saves' },
    { key: 'yellowCard',       label: 'Yellow Cards' },
    { key: 'redCard',          label: 'Red Cards' },
    { key: 'foul',             label: 'Fouls' },
  ],
  table_tennis: [
    { key: 'winner',           label: 'Winners' },
    { key: 'ace',              label: 'Service Aces' },
    { key: 'smash',            label: 'Smashes' },
    { key: 'error',            label: 'Errors' },
    { key: 'foul',             label: 'Fouls' },
  ],
};

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(val, decimals = 1) {
  if (val === null || val === undefined) return 'â€”';
  const n = parseFloat(val);
  return isNaN(n) ? 'â€”' : n % 1 === 0 ? n : n.toFixed(decimals);
}

function StatTile({ label, value, onClick }) {
  return (
    <div
      className={`pp-stat${onClick ? ' pp-stat--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <span className="pp-stat__value">{value ?? '-'}</span>
      <span className="pp-stat__label">{label}</span>
    </div>
  );
}

// â”€â”€ main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function PlayerProfile({ token, onLogout }) {
  const { userId } = useParams();
  const navigate   = useNavigate();

  const [profile,      setProfile]      = useState(null);
  const [sportsStats,  setSportsStats]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [activeTab,    setActiveTab]    = useState('overview');
  const [followLoading, setFollowLoading] = useState(false);
  const [friendLoading, setFriendLoading] = useState(false);

  const isFollowing  = profile?.is_following  ?? false;
  const friendStatus = profile?.friend_status ?? 'none';
  const blockedByMe  = profile?.blocked_by_me === true;
  const blocksMe     = profile?.blocks_me === true;
  // Either side blocked: no message, friend request or follow
  const contactBlocked = blockedByMe || blocksMe;

  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id ?? null; }
    catch { return null; }
  })();

  // isOwnProfile: works whether userId is an integer, UUID, or short_code
  // Once the profile loads, compare profile.id (integer) to currentUserId.
  // Before profile loads, fall back to parseInt for plain-integer URLs.
  const isOwnProfile = currentUserId && (
    (profile ? profile.id === currentUserId : parseInt(userId, 10) === currentUserId)
  );

  // â”€â”€ Data loading â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [profileRes, statsRes] = await Promise.allSettled([
        getPublicProfile(userId),
        apiClient.get(`/users/${userId}/sports-stats`),
      ]);

      if (profileRes.status === 'fulfilled' && profileRes.value.success) {
        setProfile(profileRes.value.data?.user ?? profileRes.value.data);
      } else {
        setError(profileRes.reason?.response?.data?.message || 'Failed to load profile');
      }

      if (statsRes.status === 'fulfilled') {
        setSportsStats(statsRes.value.data?.data?.stats || []);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // â”€â”€ Follow / Unfollow â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleFollow = async () => {
    if (!currentUserId) { navigate('/login'); return; }
    try {
      setFollowLoading(true);
      const res = await followService.follow(userId);
      // Server returns { alreadyFollowing, followerCount } — trust it rather
      // than blindly adding 1 (an "Already following" reply must not bump it).
      const { alreadyFollowing, followerCount } = res?.data || {};
      const serverCount = followerCount == null ? NaN : Number(followerCount);
      setProfile((p) => ({
        ...p,
        is_following: true,
        follower_count: Number.isFinite(serverCount)
          ? serverCount
          : (p.follower_count ?? 0) + (alreadyFollowing ? 0 : 1),
      }));
    } catch (err) { console.error('Follow error:', err); }
    finally { setFollowLoading(false); }
  };

  const handleUnfollow = async () => {
    try {
      setFollowLoading(true);
      const res = await followService.unfollow(userId);
      // Only decrement when the server says a follow row was actually removed
      const wasFollowing = res?.data?.wasFollowing !== false;
      setProfile((p) => ({
        ...p,
        is_following: false,
        follower_count: wasFollowing ? Math.max(0, (p.follower_count ?? 1) - 1) : (p.follower_count ?? 0),
      }));
    } catch (err) { console.error('Unfollow error:', err); }
    finally { setFollowLoading(false); }
  };

  // â”€â”€ Friend actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleSendFriendRequest = async () => {
    if (!currentUserId) { navigate('/login'); return; }
    try {
      setFriendLoading(true);
      const res = await friendService.sendRequest(userId);
      // The other user had already asked us → the server auto-accepts
      const autoAccepted = res?.data?.autoAccepted === true;
      setProfile((p) => ({ ...p, friend_status: autoAccepted ? 'friends' : 'pending_outgoing' }));
    } catch (err) { console.error('Friend request error:', err); }
    finally { setFriendLoading(false); }
  };

  const handleAcceptFriendRequest = async () => {
    try {
      setFriendLoading(true);
      await friendService.acceptRequest(userId);
      setProfile((p) => ({ ...p, friend_status: 'friends' }));
    } catch (err) { console.error('Accept error:', err); }
    finally { setFriendLoading(false); }
  };

  const handleRejectFriendRequest = async () => {
    try {
      setFriendLoading(true);
      await friendService.rejectRequest(userId);
      setProfile((p) => ({ ...p, friend_status: 'none' }));
    } catch (err) { console.error('Reject error:', err); }
    finally { setFriendLoading(false); }
  };

  const handleRemoveRelationship = async () => {
    // Unfriending is destructive — confirm first (cancelling a pending request is not)
    if (friendStatus === 'friends' && !window.confirm('Remove this player from your friends?')) return;
    try {
      setFriendLoading(true);
      await friendService.removeRelationship(userId);
      setProfile((p) => ({ ...p, friend_status: 'none' }));
    } catch (err) { console.error('Remove error:', err); }
    finally { setFriendLoading(false); }
  };

  // â”€â”€ Render helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function renderFriendButton() {
    if (!currentUserId || isOwnProfile) return null;
    if (contactBlocked) {
      return (
        <button className="pp-btn pp-btn--add" disabled>
          Add Friend
        </button>
      );
    }
    switch (friendStatus) {
      case 'friends':
        return (
          <button className="pp-btn pp-btn--friends" onClick={handleRemoveRelationship} disabled={friendLoading}>
            {friendLoading ? '...' : 'Friends'}
          </button>
        );
      case 'pending_outgoing':
        return (
          <button className="pp-btn pp-btn--pending" onClick={handleRemoveRelationship} disabled={friendLoading}>
            {friendLoading ? '...' : 'Pending'}
          </button>
        );
      case 'pending_incoming':
        return (
          <>
            <button className="pp-btn pp-btn--accept" onClick={handleAcceptFriendRequest} disabled={friendLoading}>
              {friendLoading ? '...' : 'Accept'}
            </button>
            <button className="pp-btn pp-btn--reject" onClick={handleRejectFriendRequest} disabled={friendLoading}>
              {friendLoading ? '...' : 'Decline'}
            </button>
          </>
        );
      default:
        return (
          <button className="pp-btn pp-btn--add" onClick={handleSendFriendRequest} disabled={friendLoading}>
            {friendLoading ? '...' : 'Add Friend'}
          </button>
        );
    }
  }

  // â”€â”€ Tab content: Overview â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function renderOverview() {
    const memberYear = profile.member_since ? new Date(profile.member_since).getFullYear() : null;
    const details = [
      { label: 'Member since',      value: memberYear ? `${memberYear}` : null },
      { label: 'Favourite Sports',  value: profile.favorite_sports },
      { label: 'Experience',        value: profile.experience },
      { label: 'Position / Role',   value: profile.position },
      { label: 'Gender',            value: profile.gender },
    ].filter((d) => d.value);

    return (
      <div className="pp-tab-content">
        {/* Stats row */}
        <div className="pp-stats-row">
          <StatTile label="Matches"   value={profile.match_count   ?? 0} />
          <StatTile label="Followers" value={profile.follower_count ?? 0} onClick={() => navigate(`/players/${profile.short_code || profile.uuid || userId}/followers`)} />
          <StatTile label="Following" value={profile.following_count ?? 0} onClick={() => navigate(`/players/${profile.short_code || profile.uuid || userId}/following`)} />
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="pp-section">
            <h3 className="pp-section__title">About</h3>
            <p className="pp-bio">{profile.bio}</p>
          </div>
        )}

        {/* Details */}
        {details.length > 0 && (
          <div className="pp-section">
            <h3 className="pp-section__title">Info</h3>
            <div className="pp-details">
              {details.map((d) => (
                <div key={d.label} className="pp-detail-item">
                  <span className="pp-detail-label">{d.label}</span>
                  <span className="pp-detail-value">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {details.length === 0 && !profile.bio && (
          <p className="pp-empty">This player hasn't filled in their profile yet.</p>
        )}
      </div>
    );
  }

  // â”€â”€ Tab content: Sport Stats â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function renderSportStats(stat) {
    const sportKey      = (stat.sport_name || '').toLowerCase().replace(/\s+/g, '_');
    const sportSpecific = stat.sport_specific_stats || {};
    const sportMetrics  = SPORT_METRICS[sportKey] || [];

    const baseRows = [
      { label: 'Games Played', value: stat.games_played ?? 0 },
      { label: 'Wins',         value: stat.games_won    ?? 0 },
      { label: 'Losses',       value: stat.games_lost   ?? 0 },
      ...(Number(stat.games_drawn) > 0 ? [{ label: 'No Result', value: Number(stat.games_drawn) }] : []),
      { label: 'Win %',        value: stat.win_percentage != null ? `${fmt(stat.win_percentage)}%` : '\u2014' },
      { label: 'Pts Scored',   value: stat.points_scored ?? 0 },
    ].filter((r) => r.value !== '\u2014');

    const specificRows = sportMetrics
      .map(({ key, label }) => ({
        label,
        value: sportSpecific[key] != null ? Number(sportSpecific[key]) : null,
      }))
      .filter((r) => r.value !== null && r.value > 0);

    // Basketball shooting splits from tracked makes/misses (PR-B13)
    const shootingRows = [];
    if (sportKey === 'basketball') {
      const shot = basketballShooting(sportSpecific);
      if (shot.fga > 0) {
        shootingRows.push({ label: 'FG', value: `${shot.fgm}/${shot.fga}` });
        shootingRows.push({ label: 'FG %', value: `${shot.fgPct}%` });
      }
      if (shot.fta > 0) {
        shootingRows.push({ label: 'FT %', value: `${shot.ftPct}%` });
      }
    }

    const rows = [...baseRows, ...shootingRows, ...specificRows];

    return (
      <div className="pp-tab-content">
        <div className="pp-sport-header">
          {stat.icon_url && <img src={stat.icon_url} alt="" className="pp-sport-icon" />}
          <div>
            <h3 className="pp-sport-name">{stat.sport_name}</h3>
            {/* All-time totals: there is no season split, so label them Career (ST-B10) */}
            <p className="pp-sport-season">Career</p>
          </div>
        </div>

        <div className="pp-stats-grid">
          {rows.map((r) => (
            <div key={r.label} className="pp-stat-card">
              <span className="pp-stat-card__value">{r.value}</span>
              <span className="pp-stat-card__label">{r.label}</span>
            </div>
          ))}
        </div>

        {rows.length === 0 && (
          <p className="pp-empty">No recorded stats yet for {stat.sport_name}.</p>
        )}
      </div>
    );
  }

  // â”€â”€ Loading / Error states â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <GameLayout token={token} onLogout={onLogout}>
        <div className="pp-state pp-state--loading">Loading profile...</div>
      </GameLayout>
    );
  }

  if (error || !profile) {
    return (
      <GameLayout token={token} onLogout={onLogout}>
        <div className="pp-state pp-state--error">
          <p>{error || 'Profile not found'}</p>
          <button className="pp-btn" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </GameLayout>
    );
  }

  // â”€â”€ Build tab list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const tabs = [
    { id: 'overview', label: 'Overview' },
    ...sportsStats.map((s) => ({ id: `sport-${s.sport_id}`, label: s.sport_name })),
  ];

  const avatarDisplay = profile.avatar_url
    ? <img src={profile.avatar_url} alt="avatar" className="pp-avatar-img" />
    : <div className="pp-avatar-placeholder">{profile.first_name?.[0]?.toUpperCase() ?? '?'}</div>;

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="pp">
        {/* Back */}
        <button className="pp-back" onClick={() => navigate(-1)}>Back</button>

        {/* Hero card */}
        <div className="pp-hero">
          <div className="pp-avatar">{avatarDisplay}</div>

          <div className="pp-hero__info">
            <h1 className="pp-name">{profile.first_name} {profile.last_name}</h1>
            <p className="pp-username">@{profile.username}</p>
          </div>

          {/* Action buttons */}
          {!isOwnProfile && currentUserId && (
            <div className="pp-actions">
              <button
                className={`pp-btn ${isFollowing ? 'pp-btn--following' : 'pp-btn--follow'}`}
                onClick={isFollowing ? handleUnfollow : handleFollow}
                disabled={followLoading || (contactBlocked && !isFollowing)}
              >
                {followLoading ? '...' : isFollowing ? 'Following' : '+ Follow'}
              </button>

              {renderFriendButton()}

              <button
                className="pp-btn pp-btn--message"
                onClick={() => navigate(`/messages/${userId}`)}
                disabled={contactBlocked}
              >
                Message
              </button>

              <PlayerSafetyMenu
                userId={profile.id ?? userId}
                name={`${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username}
                blockedByMe={blockedByMe}
                mutedByMe={profile.muted_by_me === true}
                onChange={(patch) => setProfile((p) => ({
                  ...p,
                  ...patch,
                  // Blocking removed my follow of them
                  ...(patch.is_following === false && p.is_following
                    ? { follower_count: Math.max(0, (p.follower_count ?? 1) - 1) }
                    : {}),
                }))}
              />

              {blockedByMe && (
                <p className="pp-blocked-note" data-testid="pp-blocked-note">
                  You blocked this player. Unblock them to message, follow or add them.
                </p>
              )}
              {!blockedByMe && blocksMe && (
                <p className="pp-blocked-note" data-testid="pp-blocked-note">
                  You can't contact this player.
                </p>
              )}
            </div>
          )}

          {isOwnProfile && (
            <button className="pp-btn pp-btn--edit" onClick={() => navigate('/profile')}>
              Edit Profile
            </button>
          )}
        </div>

        {/* Tab bar */}
        {tabs.length > 1 && (
          <div className="pp-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`pp-tab ${activeTab === tab.id ? 'pp-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab content */}
        {activeTab === 'overview' && renderOverview()}
        {sportsStats.map((stat) =>
          activeTab === `sport-${stat.sport_id}` ? (
            <React.Fragment key={stat.sport_id}>{renderSportStats(stat)}</React.Fragment>
          ) : null
        )}
      </div>
    </GameLayout>
  );
}

export default PlayerProfile;
